import { CELL_RAW, GRID_SCALE, computeTrackBounds } from './Track.js';

const SIZE = 180;
const MARGIN = 10;

export default class Minimap {

	constructor( cells ) {

		this.players = new Map();
		this.selfId = null;
		this.bounds = computeTrackBounds( cells );

		const style = document.createElement( 'style' );
		style.textContent = `
			#minimap {
				position: absolute;
				bottom: 188px;
				right: 12px;
				width: ${ SIZE }px;
				height: ${ SIZE }px;
				background: rgba(0,0,0,0.5);
				border-radius: 12px;
				padding: 0;
				z-index: 10;
				pointer-events: none;
				user-select: none;
				backdrop-filter: blur(8px);
				-webkit-backdrop-filter: blur(8px);
				overflow: hidden;
			}
			@media (max-width: 820px) {
				#minimap {
					bottom: 208px;
					right: 10px;
					width: 148px;
					height: 148px;
				}
			}
			#minimap canvas { display: block; width: 100%; height: 100%; }
		`;
		document.head.appendChild( style );
		this._style = style;

		const el = document.createElement( 'div' );
		el.id = 'minimap';

		const canvas = document.createElement( 'canvas' );
		const dpr = Math.max( 1, Math.min( 2, window.devicePixelRatio || 1 ) );
		canvas.width = SIZE * dpr;
		canvas.height = SIZE * dpr;
		canvas.style.width = SIZE + 'px';
		canvas.style.height = SIZE + 'px';
		el.appendChild( canvas );
		document.body.appendChild( el );

		this._el = el;
		this._canvas = canvas;
		this._ctx = canvas.getContext( '2d' );
		this._ctx.scale( dpr, dpr );

		this._track = this._renderTrack( cells );

	}

	_project( worldX, worldZ ) {

		const half = SIZE / 2 - MARGIN;
		const cx = ( worldX - this.bounds.centerX ) / this.bounds.halfWidth * half + SIZE / 2;
		const cz = - ( worldZ - this.bounds.centerZ ) / this.bounds.halfDepth * half + SIZE / 2;
		return [ cx, cz ];

	}

	_renderTrack( cells ) {

		const off = document.createElement( 'canvas' );
		const dpr = Math.max( 1, Math.min( 2, window.devicePixelRatio || 1 ) );
		off.width = SIZE * dpr;
		off.height = SIZE * dpr;
		const ctx = off.getContext( '2d' );
		ctx.scale( dpr, dpr );

		ctx.fillStyle = '#5a6268';

		const cellWorld = CELL_RAW * GRID_SCALE;
		const halfX = SIZE / 2 - MARGIN;
		const halfZ = SIZE / 2 - MARGIN;
		const sizeX = cellWorld / this.bounds.halfWidth * halfX;
		const sizeZ = cellWorld / this.bounds.halfDepth * halfZ;
		const w = Math.max( 2, sizeX * 0.92 );
		const h = Math.max( 2, sizeZ * 0.92 );
		const r = Math.min( w, h ) * 0.25;

		for ( const c of cells ) {

			const name = c[ 2 ];
			if ( typeof name !== 'string' || ! name.startsWith( 'track-' ) ) continue;

			const gx = c[ 0 ];
			const gz = c[ 1 ];
			const wx = ( gx + 0.5 ) * cellWorld;
			const wz = ( gz + 0.5 ) * cellWorld;
			const [ px, py ] = this._project( wx, wz );

			roundRect( ctx, px - w / 2, py - h / 2, w, h, r );
			ctx.fill();

		}

		return off;

	}

	setSelf( id ) {

		this.selfId = id;

	}

	setPlayer( id, x, z, heading ) {

		this.players.set( id, { x, z, heading } );

	}

	removePlayer( id ) {

		this.players.delete( id );

	}

	update() {

		const ctx = this._ctx;
		ctx.clearRect( 0, 0, SIZE, SIZE );
		ctx.drawImage( this._track, 0, 0, SIZE, SIZE );

		for ( const [ id, p ] of this.players ) {

			const [ cx, cz ] = this._project( p.x, p.z );
			const isSelf = id === this.selfId;
			const color = isSelf ? '#5af168' : '#ffd166';

			const dx = Math.sin( p.heading ) * 4;
			const dz = - Math.cos( p.heading ) * 4;

			ctx.strokeStyle = color;
			ctx.lineWidth = 1.5;
			ctx.beginPath();
			ctx.moveTo( cx, cz );
			ctx.lineTo( cx + dx, cz + dz );
			ctx.stroke();

			ctx.fillStyle = color;
			ctx.beginPath();
			ctx.arc( cx, cz, 3, 0, Math.PI * 2 );
			ctx.fill();

		}

	}

	dispose() {

		if ( this._el && this._el.parentNode ) this._el.parentNode.removeChild( this._el );
		if ( this._style && this._style.parentNode ) this._style.parentNode.removeChild( this._style );
		this._el = null;
		this._style = null;
		this._canvas = null;
		this._ctx = null;
		this._track = null;
		this.players.clear();

	}

}

function roundRect( ctx, x, y, w, h, r ) {

	const rr = Math.min( r, w / 2, h / 2 );
	ctx.beginPath();
	ctx.moveTo( x + rr, y );
	ctx.lineTo( x + w - rr, y );
	ctx.quadraticCurveTo( x + w, y, x + w, y + rr );
	ctx.lineTo( x + w, y + h - rr );
	ctx.quadraticCurveTo( x + w, y + h, x + w - rr, y + h );
	ctx.lineTo( x + rr, y + h );
	ctx.quadraticCurveTo( x, y + h, x, y + h - rr );
	ctx.lineTo( x, y + rr );
	ctx.quadraticCurveTo( x, y, x + rr, y );
	ctx.closePath();

}
